import { X, TrendingUp, Users, Target, Lightbulb, Award } from "lucide-react";
import { cn } from "../common/cn";
import { useDebateStore } from "../../store/debateStore";
import type { ComparisonKeyDifference } from "../../domain/types";

const WINNER_BADGE: Record<string, { label: string; cls: string }> = {
  a: { label: "A", cls: "bg-blue-500/20 text-blue-400" },
  b: { label: "B", cls: "bg-emerald-500/20 text-emerald-400" },
  tie: { label: "Eşit", cls: "bg-yellow-500/20 text-yellow-400" },
};

function BarVisualization({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 truncate text-fg-muted">{label}</span>
      <div className="flex-1 h-4 rounded bg-surface-2/60">
        <div
          className={cn("h-full rounded transition-all duration-500", color)}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span className="w-8 text-right font-mono text-fg-muted">
        {value.toFixed(1)}
      </span>
    </div>
  );
}

function DifferenceRow({ d }: { d: ComparisonKeyDifference }) {
  // `WINNER_BADGE.tie` is statically present so the fallback is non-null
  // by construction; the explicit cast suppresses TS's structural
  // narrowing limitation around computed-key index access.
  const badge = (WINNER_BADGE[d.winner] ?? WINNER_BADGE.tie) as {
    cls: string;
    label: string;
  };
  return (
    <tr className="border-b border-line-soft/30">
      <td className="py-2 pr-2 text-xs font-medium text-fg-strong">
        {d.aspect}
      </td>
      <td className="py-2 px-2 text-xs text-fg-muted">{d.debate_a}</td>
      <td className="py-2 px-2 text-xs text-fg-muted">{d.debate_b}</td>
      <td className="py-2 pl-2">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium",
            badge.cls,
          )}
        >
          {badge.label}
        </span>
      </td>
    </tr>
  );
}

export default function ComparisonReportView() {
  const report = useDebateStore((s) => s.comparisonReport);
  const clear = useDebateStore((s) => s.clearCompareSelection);
  const loading = useDebateStore((s) => s.versionsLoading);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-fg-muted">
        <div className="animate-spin mr-2 h-5 w-5 border-2 border-accent-400 border-t-transparent rounded-full" />
        AI kıyaslama raporu oluşturuluyor…
      </div>
    );
  }

  if (!report) return null;

  const { verdict_quality_scores: vqs } = report;
  const maxScore = 10;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-line-soft/40 bg-surface-1/80 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-fg-strong flex items-center gap-1.5">
          <Target className="h-4 w-4 text-accent-400" />
          AI Kıyaslama Raporu
        </h3>
        <button
          onClick={clear}
          className="text-fg-muted hover:text-fg-strong transition"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Summary */}
      <div className="rounded-lg bg-accent-500/5 border border-accent-500/20 p-3">
        <p className="text-sm text-fg-strong leading-relaxed">
          {report.summary}
        </p>
      </div>

      {/* Key Differences Table */}
      {report.key_differences.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> Temel Farklar
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line-soft/50">
                  <th className="py-1.5 text-[10px] font-semibold text-fg-muted uppercase">
                    Boyut
                  </th>
                  <th className="py-1.5 px-2 text-[10px] font-semibold text-blue-400 uppercase">
                    Tartışma A
                  </th>
                  <th className="py-1.5 px-2 text-[10px] font-semibold text-emerald-400 uppercase">
                    Tartışma B
                  </th>
                  <th className="py-1.5 text-[10px] font-semibold text-fg-muted uppercase">
                    Kazanan
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.key_differences.map((d, i) => (
                  <DifferenceRow key={i} d={d} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Verdict Quality Scores */}
      <div>
        <h4 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2 flex items-center gap-1">
          <Award className="h-3 w-3" /> Karar Kalite Skorları
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] font-semibold text-blue-400 mb-1.5">
              Tartışma A
            </p>
            <div className="flex flex-col gap-1.5">
              <BarVisualization
                label="Tamlık"
                value={vqs.debate_a.completeness}
                max={maxScore}
                color="bg-blue-500/60"
              />
              <BarVisualization
                label="Uygulanabilirlik"
                value={vqs.debate_a.actionability}
                max={maxScore}
                color="bg-blue-500/60"
              />
              <BarVisualization
                label="Risk Kapsamı"
                value={vqs.debate_a.risk_coverage}
                max={maxScore}
                color="bg-blue-500/60"
              />
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-emerald-400 mb-1.5">
              Tartışma B
            </p>
            <div className="flex flex-col gap-1.5">
              <BarVisualization
                label="Tamlık"
                value={vqs.debate_b.completeness}
                max={maxScore}
                color="bg-emerald-500/60"
              />
              <BarVisualization
                label="Uygulanabilirlik"
                value={vqs.debate_b.actionability}
                max={maxScore}
                color="bg-emerald-500/60"
              />
              <BarVisualization
                label="Risk Kapsamı"
                value={vqs.debate_b.risk_coverage}
                max={maxScore}
                color="bg-emerald-500/60"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Persona Impact */}
      {report.persona_impact.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2 flex items-center gap-1">
            <Users className="h-3 w-3" /> Persona Etki Analizi
          </h4>
          <div className="flex flex-col gap-2">
            {report.persona_impact.map((p, i) => (
              <div key={i} className="rounded-lg bg-surface-2/40 p-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-fg-strong">
                    {p.persona}
                  </span>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-blue-400">
                      A: {(p.debate_a_influence * 100).toFixed(0)}%
                    </span>
                    <span className="text-emerald-400">
                      B: {(p.debate_b_influence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
                {p.note && (
                  <p className="text-[10px] text-fg-muted/70">{p.note}</p>
                )}
                <div className="mt-1 flex gap-1 h-2">
                  <div
                    className="bg-blue-500/50 rounded"
                    style={{ width: `${p.debate_a_influence * 100}%` }}
                  />
                  <div
                    className="bg-emerald-500/50 rounded"
                    style={{ width: `${p.debate_b_influence * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strategic Insights */}
      {report.strategic_insights.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2 flex items-center gap-1">
            <Lightbulb className="h-3 w-3" /> Stratejik İçgörüler
          </h4>
          <ul className="flex flex-col gap-1.5">
            {report.strategic_insights.map((insight, i) => (
              <li
                key={i}
                className="flex items-start gap-1.5 text-xs text-fg-muted"
              >
                <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-accent-400 shrink-0" />
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendation */}
      <div className="rounded-lg border-2 border-accent-500/30 bg-accent-500/5 p-3">
        <h4 className="text-xs font-semibold text-accent-400 uppercase tracking-wider mb-1.5">
          Tavsiye
        </h4>
        <p className="text-sm text-fg-strong leading-relaxed">
          {report.recommendation}
        </p>
      </div>
    </div>
  );
}
